import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import ManageHistoryOutlinedIcon from "@mui/icons-material/ManageHistoryOutlined";
import Navbar from "../components/common/Navbar";
import DocumentsPageHeader from "../components/common/DocumentsPageHeader";
import { AuthContext } from "../context/Auth/AuthContext";
import { getAuditTrail, getDocumentAuditTrail } from "../api/auditTrail";
import "../styles/Documents.css";

// Document Audit Trail - read-only activity built from the data the DMS
// already stores (uploads, updates, file versions, OCR runs, request links
// and approvals/rejections, separations, trash). Only stored values are
// shown: when just the latest value exists the event reads "Document
// updated" without a previous value.

const RANGES = [
  { value: "today", label: "Today" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
];

const RANGE_TEXT = { today: "today", 7: "in 7 days", 30: "in 30 days", 60: "in 60 days" };

const HISTORY_TABS = [
  { value: "all", label: "All" },
  { value: "uploaded", label: "Uploaded" },
  { value: "edited", label: "Edited" },
  { value: "ocr", label: "OCR Run" },
  { value: "status", label: "Status" },
];

const CHIP = {
  uploaded: ["Uploaded", "bg-emerald-50 text-emerald-700"],
  edited: ["Edited", "bg-blue-50 text-blue-700"],
  ocr: ["OCR Run", "bg-[#eefaf8] text-[#0f766e]"],
  status: ["Status", "bg-amber-50 text-amber-700"],
};

const typeBadge = (type) => (type ? String(type).slice(0, 4).toUpperCase() : "DOC");

const formatDay = (ymd) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

const formatTime = (at) =>
  new Date(at.replace(" ", "T")).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const formatDateTime = (at) =>
  new Date(at.replace(" ", "T")).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const StatCard = ({ value, label, tone = "" }) => (
  <div className="rounded-[14px] border border-gray-200 bg-white px-[18px] py-4">
    <div className={`text-2xl font-extrabold leading-tight tracking-tight ${tone}`}>{value}</div>
    <div className="mt-0.5 text-xs font-semibold text-gray-500">{label}</div>
  </div>
);

const TypeIcon = ({ type, size = "h-8 w-8 text-[10px]" }) => (
  <span className={`flex shrink-0 items-center justify-center rounded-lg bg-[#1e293f] font-extrabold text-white ${size}`}>
    {typeBadge(type)}
  </span>
);

const DocumentHistoryDialog = ({ documentId, onClose, onOpenDocument, authParams }) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!documentId) return;
    let active = true;
    setLoading(true);
    setError("");
    setData(null);
    setTab("all");
    setFilter("");
    getDocumentAuditTrail({ token, documentId, ...authParams }).then((res) => {
      if (!active) return;
      if (res?.status === "success") setData(res.data);
      else setError(res?.error || "Could not load the document history.");
      setLoading(false);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const events = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data?.events || []).filter(
      (e) =>
        (tab === "all" || e.type === tab) &&
        (!q ||
          [e.title, e.field, e.detail, e.old_value, e.new_value, e.user_name]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q))
    );
  }, [data, tab, filter]);

  const doc = data?.document;

  return (
    <Dialog open={!!documentId} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "14px" } }}>
      <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-4">
        <TypeIcon type={doc?.document_type} size="h-9 w-9 text-[11px]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-extrabold text-gray-900">{doc?.document_name || "Document history"}</div>
          <div className="truncate text-xs text-gray-500">
            {[doc?.client_name, doc?.client_reference_number, doc?.in_trash ? "In trash" : null].filter(Boolean).join(" · ") ||
              "Full history"}
          </div>
        </div>
        {doc && !doc.in_trash && (
          <button
            type="button"
            onClick={() => onOpenDocument(doc.document_id)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-[#0f766e] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#0b5f58]"
          >
            Open Document <NorthEastIcon sx={{ fontSize: 13 }} />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200"
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-5 py-3">
        <div className="inline-flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
          {HISTORY_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-bold ${
                tab === t.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[200px] flex-1">
          <SearchOutlinedIcon sx={{ fontSize: 15 }} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter this document's history by event, field or user…"
            className="h-9 w-full rounded-lg border border-gray-200 pl-8 pr-3 text-xs outline-none focus:border-[#149188]"
          />
        </div>
        <span className="text-xs font-semibold text-gray-500">Full history</span>
      </div>

      <div className="max-h-[440px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <CircularProgress size={24} sx={{ color: "#0f766e" }} />
          </div>
        ) : error ? (
          <p className="py-10 text-center text-sm text-red-600">{error}</p>
        ) : (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="sticky top-0 z-[1] bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wide text-gray-400">
                <th className="px-5 py-2.5">Action</th>
                <th className="px-3 py-2.5">Event</th>
                <th className="px-3 py-2.5">Change</th>
                <th className="px-3 py-2.5">Changed By</th>
                <th className="px-5 py-2.5">Date &amp; Time</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-gray-400">
                    No history matches.
                  </td>
                </tr>
              )}
              {events.map((e, i) => {
                const [chipLabel, chipClass] = CHIP[e.type] || CHIP.edited;
                const hasChange = e.field && (e.old_value || e.new_value);
                return (
                  <tr key={`${e.at}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-2.5">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${chipClass}`}>{chipLabel}</span>
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-gray-700">{e.title}</td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-gray-800">
                      {hasChange ? (
                        <>
                          <span className="mr-1 font-sans text-gray-500">{e.field}:</span>
                          {e.old_value && (
                            <>
                              <span className="text-gray-400 line-through">{e.old_value}</span>
                              <span className="mx-1 text-gray-300">→</span>
                            </>
                          )}
                          {e.new_value}
                        </>
                      ) : null}
                      {e.detail && <div className="font-sans text-[11px] text-gray-500">{e.detail}</div>}
                      {!hasChange && !e.detail && <span className="font-sans text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-gray-700">
                      {e.user_name || <span className="font-normal text-gray-400">Not recorded</span>}
                    </td>
                    <td className="whitespace-nowrap px-5 py-2.5 text-gray-500">{formatDateTime(e.at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Dialog>
  );
};

const AuditTrail = () => {
  const { token, id, is_admin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [range, setRange] = useState("today");
  const [documentType, setDocumentType] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [openDocumentId, setOpenDocumentId] = useState(null);

  const authParams = useMemo(
    () => ({ isAdmin: is_admin, userId: id, roleId: is_admin === "1" ? localStorage.getItem("role_id") : null }),
    [is_admin, id]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getAuditTrail({ token, range, documentType, search: debouncedSearch, ...authParams }).then((res) => {
      if (!active) return;
      if (res?.status === "success") setData(res.data);
      else setError(res?.error || "Could not load the audit trail.");
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [token, range, documentType, debouncedSearch, authParams]);

  const stats = data?.stats;
  const rangeText = RANGE_TEXT[range];

  return (
    <div className="document-container">
      <div className="navbar">
        <Navbar />
      </div>
      {/* shrink-0: .document-container is a fixed-height scrolling flex
          column, so without it this box shrinks to the viewport and its
          background stops while the activity rows overflow past it. */}
      <div className="document-content shrink-0">
        <div className="mx-auto w-full max-w-[1200px] px-6">
        <DocumentsPageHeader
          showBreadcrumb={false}
          breadcrumbs={[{ label: "Audit Trail" }]}
          icon={ManageHistoryOutlinedIcon}
          title="Document Audit Trail"
          description="Every recorded action on a document — uploads, updates, OCR runs, request links and approvals — grouped by day."
          headerSpacingClass="py-3"
        />

        <div className="flex flex-col gap-4 pb-8 pt-3">
          {/* Filters */}
          <div className="rounded-[14px] border border-gray-200 bg-white px-[18px] py-4">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="inline-flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
                {RANGES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRange(r.value)}
                    className={`rounded-md px-3.5 py-[7px] text-[12.5px] font-bold ${
                      range === r.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="ml-auto h-[38px] min-w-[200px] rounded-lg border border-gray-200 bg-white px-3 text-[12.5px] font-semibold text-gray-800 outline-none focus:border-[#149188]"
                aria-label="Document type"
              >
                <option value="">All document types</option>
                {(data?.document_types || []).map((t) => (
                  <option key={t} value={t}>
                    {t.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative mt-3">
              <SearchOutlinedIcon sx={{ fontSize: 16 }} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by document name, client, reference number or user…"
                className="h-10 w-full rounded-lg border border-gray-200 pl-9 pr-3 text-[13px] outline-none focus:border-[#149188] focus:ring-2 focus:ring-[#149188]/10"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
            <StatCard value={stats?.documents_added ?? "–"} label={`Documents added ${rangeText}`} tone="text-emerald-700" />
            <StatCard value={stats?.documents_updated ?? "–"} label={`Documents updated ${rangeText}`} tone="text-blue-600" />
            <StatCard value={stats?.users_active ?? "–"} label={`Users active ${rangeText}`} />
            <StatCard value={stats?.total_changes ?? "–"} label="Total changes in range" />
          </div>

          {/* Days */}
          {loading && !data ? (
            <div className="flex justify-center py-12">
              <CircularProgress size={26} sx={{ color: "#0f766e" }} />
            </div>
          ) : error ? (
            <p className="py-10 text-center text-sm text-red-600">{error}</p>
          ) : data && data.days.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white py-12 text-center text-sm text-gray-500">
              No document activity {rangeText}.
            </div>
          ) : (
            (data?.days || []).map((day) => (
              <section key={day.date} className={loading ? "opacity-60" : ""}>
                <div className="mb-2.5 mt-2 flex items-baseline gap-2.5">
                  {day.is_today && (
                    <span className="rounded-full bg-[#eefaf8] px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-[#0f766e]">
                      Today
                    </span>
                  )}
                  <span className="text-[13.5px] font-bold text-gray-900">{formatDay(day.date)}</span>
                  <span className="ml-auto text-xs font-semibold text-gray-500">
                    {day.changes} change{day.changes === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="overflow-hidden rounded-[14px] border border-gray-200 bg-white">
                  {day.items.map((item) => (
                    <div
                      key={item.document_id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenDocumentId(item.document_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setOpenDocumentId(item.document_id);
                        }
                      }}
                      className={`flex w-full cursor-pointer items-center gap-3 border-b border-l-[3px] border-b-gray-100 px-4 py-[11px] text-left leading-tight last:border-b-0 ${
                        day.is_today
                          ? "border-l-[#149188] bg-[#eefaf8] hover:bg-[#e6f6f4]"
                          : "border-l-transparent hover:bg-gray-50"
                      }`}
                    >
                      <TypeIcon type={item.document_type} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-bold text-gray-900">{item.document_name}</div>
                        <div className="mt-[3px] truncate text-[11.5px] text-gray-500">
                          {[item.latest_title, item.latest_detail, item.client_name, item.latest_user ? `by ${item.latest_user}` : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      <div className="shrink-0 whitespace-nowrap text-xs font-semibold text-gray-500">
                        {item.added > 0 && <b className="font-bold text-emerald-700">{item.added} added</b>}
                        {item.added > 0 && item.updated > 0 && " · "}
                        {item.updated > 0 && <b className="font-bold text-blue-600">{item.updated} updated</b>}
                      </div>
                      <ChevronRightIcon sx={{ fontSize: 18 }} className="shrink-0 text-gray-300" />
                      <div className="w-16 shrink-0 text-right text-xs font-semibold text-gray-500">{formatTime(item.latest_at)}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
        </div>
      </div>

      <DocumentHistoryDialog
        documentId={openDocumentId}
        onClose={() => setOpenDocumentId(null)}
        authParams={authParams}
        onOpenDocument={(docId) => navigate(`/documents?openDocument=${encodeURIComponent(docId)}`)}
      />
    </div>
  );
};

export default AuditTrail;
